'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useSWRMutation from 'swr/mutation';
import type { AuthTokens } from '../lib/cookieUtils';
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from '../lib/cookieUtils';
import { initiateLogin, logoutUser, verifyOtp } from '../lib/authApi';
import { setupFetchInterceptor } from '../lib/fetchInterceptor';
export type AuthStatus = 'loading' | 'unauthenticated' | 'sending' | 'awaiting-otp' | 'verifying' | 'authenticated' | 'logging-out' | 'expired';

// Stable states stored in React state.
// Transient states (sending, verifying, logging-out) are derived from SWR isMutating flags.
type SessionStatus = Extract<AuthStatus, 'loading' | 'unauthenticated' | 'awaiting-otp' | 'authenticated' | 'expired'>;
export type AuthContextType = {
  status: AuthStatus;
  error: string | null;
  initiateLogin: (email: string) => Promise<void>;
  verifyOtp: (email: string, otp: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
};
const AuthContext = createContext<AuthContextType | null>(null);
type AuthProviderProps = PropsWithChildren<{
  onSessionExpired?: () => void;
}>;
export function AuthProvider({
  children,
  onSessionExpired
}: AuthProviderProps) {
  // Only stable states live in React state. Transient loading states come from SWR.
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>('loading');
  const {
    trigger: triggerInitiateLogin,
    isMutating: isSending,
    error: initiateError,
    reset: resetInitiateLogin
  } = useSWRMutation<void, Error, string, string>('auth/initiate', async (_key, {
    arg: email
  }) => {
    await initiateLogin(email);
  }, {
    onSuccess: () => setSessionStatus('awaiting-otp'),
    onError: () => setSessionStatus('unauthenticated'),
    throwOnError: false
  });
  const {
    trigger: triggerVerifyOtp,
    isMutating: isVerifying,
    error: verifyError,
    reset: resetVerifyOtp
  } = useSWRMutation<AuthTokens, Error, string, {
    email: string;
    otp: string;
  }>('auth/verify', async (_key_0, {
    arg
  }) => {
    const authTokens = await verifyOtp(arg.email, arg.otp);
    setTokens(authTokens); // write to cookies (source of truth)
    return authTokens;
  }, {
    onSuccess: () => setSessionStatus('authenticated'),
    onError: () => setSessionStatus('awaiting-otp'),
    throwOnError: false
  });
  const {
    trigger: triggerLogout,
    isMutating: isLoggingOut
  } = useSWRMutation('auth/logout', async () => {
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();
    // Only call server logout if we have an access token
    // (refresh token may be missing in passwordless auth)
    if (accessToken) {
      try {
        await logoutUser(accessToken, refreshToken || '');
      } catch {
        // Clear local session regardless of server response
      }
    }
    clearTokens();
  }, {
    onSuccess: () => setSessionStatus('unauthenticated')
  });

  // Merge stable session state with transient SWR mutation states.
  const status = useMemo<AuthStatus>(() => {
    if (isSending) {
      return 'sending';
    }
    if (isVerifying) {
      return 'verifying';
    }
    if (isLoggingOut) {
      return 'logging-out';
    }
    return sessionStatus;
  }, [isSending, isVerifying, isLoggingOut, sessionStatus]);

  // Merge per-mutation SWR errors into a single error string.
  const error = useMemo<string | null>(() => {
    if (initiateError instanceof Error) {
      return initiateError.message;
    }
    if (verifyError instanceof Error) {
      return verifyError.message;
    }
    return null;
  }, [initiateError, verifyError]);

  // --- Fetch interceptor ---

  const refreshSuccessHandler = useCallback((_newTokens: AuthTokens) => {
    // Tokens updated in cookies by interceptor — no state update needed
  }, []);
  const refreshFailureHandler = useCallback(() => {
    clearTokens();
    setSessionStatus('expired');
    onSessionExpired?.();
  }, [onSessionExpired]);

  // Initialize from cookies on mount.
  // The browser manages cookie expiry via Max-Age — if getAccessToken() returns
  // a value the cookie is still within its validity window. JWT-level expiry
  // is handled proactively by useAuthSession once the status is 'authenticated'.
  //
  // Note: Some auth flows (like passwordless) may not provide a refresh_token.
  // We only check for access_token here, as refresh_token is optional.
  useEffect(() => {
    const accessToken_0 = getAccessToken();
    if (accessToken_0) {
      setSessionStatus('authenticated');
      return;
    }
    setSessionStatus('unauthenticated');
  }, []);

  // Set up fetch interceptor once when authenticated.
  // Tokens are read dynamically via callbacks — no need to recreate on refresh.
  useEffect(() => {
    if (status !== 'authenticated') {
      return;
    }
    const cleanup = setupFetchInterceptor({
      getAccessToken: () => getAccessToken(),
      getRefreshToken: () => getRefreshToken(),
      onRefreshSuccess: refreshSuccessHandler,
      onRefreshFailure: refreshFailureHandler
    });
    return cleanup;
  }, [status, refreshSuccessHandler, refreshFailureHandler]);

  // --- Public API ---

  const initiateLoginHandler = useCallback(async (email_0: string): Promise<void> => {
    resetVerifyOtp(); // clear stale OTP errors when starting a new login
    await triggerInitiateLogin(email_0);
  }, [resetVerifyOtp, triggerInitiateLogin]);
  const verifyOtpHandler = useCallback(async (email_1: string, otp: string): Promise<void> => {
    await triggerVerifyOtp({
      email: email_1,
      otp
    });
  }, [triggerVerifyOtp]);
  const logoutHandler = useCallback(async (): Promise<void> => {
    await triggerLogout();
  }, [triggerLogout]);
  const clearError = useCallback(() => {
    resetInitiateLogin();
    resetVerifyOtp();
  }, [resetInitiateLogin, resetVerifyOtp]);
  const value = useMemo<AuthContextType>(() => ({
    status,
    error,
    initiateLogin: initiateLoginHandler,
    verifyOtp: verifyOtpHandler,
    logout: logoutHandler,
    clearError
  }), [status, error, initiateLoginHandler, verifyOtpHandler, logoutHandler, clearError]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
export function useAuthProvider() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthProvider must be used within an AuthProvider");
  }
  return context;
}